import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {UserIcon} from "@heroicons/react/outline";
import {PlusIcon, UserIcon as UserIconSolid} from "@heroicons/react/solid";
import {formattedDateTime} from "services/date";
import {User} from "types/User";
import {useNavigate} from "react-router-dom";
import DeleteUser from "pages/User/Delete";
import SearchBar from "components/SearchBar";
import BooleanIndicator from "components/BooleanIndicator";
import Button from "components/Button";
import useSorting from "../../../hooks/useSorting";
import {SortDirection} from "../../../types/SortDirection";
import AudienceMemberContextMenu from "../../../components/menus/AudienceMemberContextMenu";
import EmailVerificationStatus from "../../../components/EmailVerificationStatus";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";

function AudienceMemberList() {
  const navigate = useNavigate();
  const auth = useAuth();

  const {currentSort, queryString: sort, sortBy} = useSorting();
  const [search, setSearch] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);

  const {data: roles, isLoading: isLoadingRoles} = useQuery(['roles'], () => ngage.roles.all());

  const audienceRoleId = roles?.find(role => role.name === 'audience')?.id;

  const users = useQuery(
    ['users.audience', {search, sort, page, roles: String(audienceRoleId)}],
    () => ngage.users.filtered({search, roles: String(audienceRoleId), sort, page}),
    {keepPreviousData: true, enabled: !!audienceRoleId}
  )

  const [deletingAudienceMember, setDeletingAudienceMember] = useState<User | null>(null);

  // Prefetch next and previous pages
  prefetch('users.audience', {sort, page, search, roles: '4'}, ngage.users.filtered)

  // Prepare meta data
  const meta = [
    {icon: UserIconSolid, value: users.data?.meta.total ? users.data.meta.total.toLocaleString() + ' total accounts' : ''}
  ]

  const breadcrumbs = {'Engagement': '/', 'Audience': '/audience'}
  const actions = <Button icon={PlusIcon} onClick={() => navigate('/audience/new')}>New</Button>

  // Prevent unauthorised viewing
  if (auth.cannot('users:read')) {
    return <NotAuthorised/>
  }

  return (
    <Page
      title="Audience" meta={meta}
      subTitle='Registered and unregistered app user accounts'
      breadcrumbs={breadcrumbs}
      actions={actions}
    >
      {/* Search and filtering */}
      <div className="mb-4 w-full grid md:grid-cols-4">
        <SearchBar isSearching={users.isFetching} onChange={setSearch}/>
      </div>

      <Table paginationData={users.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader
              sortable
              sortDirection={currentSort.name ?? SortDirection.NONE}
              onSort={direction => sortBy('name', direction)}
            >
              Name
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.email ?? SortDirection.NONE}
              onSort={direction => sortBy('email', direction)}>
              Email
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.ticket_count ?? SortDirection.NONE}
              onSort={direction => sortBy('ticket_count', direction)}
              alignment={TableHeader.alignments.CENTER}>
              Tickets
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.membership_count ?? SortDirection.NONE}
              onSort={direction => sortBy('membership_count', direction)}
              alignment={TableHeader.alignments.CENTER}>
              Memberships
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.visit_count ?? SortDirection.NONE}
              onSort={direction => sortBy('visit_count', direction)}
              alignment={TableHeader.alignments.CENTER}>
              Visited
            </TableHeader>
            <TableHeader
              alignment={TableHeader.alignments.CENTER}>
              App Permissions
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.linked_account_count ?? SortDirection.NONE}
              onSort={direction => sortBy('linked_account_count', direction)}
              alignment={TableHeader.alignments.CENTER}>
              Linked
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.created_at ?? SortDirection.NONE}
              onSort={direction => sortBy('created_at', direction)}>
              Created
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.last_logged_in_at ?? SortDirection.NONE}
              onSort={direction => sortBy('last_logged_in_at', direction)}>
              Last Login
            </TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={isLoadingRoles || users.isLoading}>

          {users?.data && users.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={10}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <UserIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No audience members</span>
                </div>
              </TableData>
            </TableRow>
            : users?.data?.data.map((audienceMember, index) => (
              <TableRow key={audienceMember.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <AudienceMemberContextMenu
                        audienceMember={audienceMember}
                        onDelete={() => setDeletingAudienceMember(audienceMember)}
                        position="left"
                      />
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <UserIcon className="h-5 w-5 mr-1 text-gray-500"/> {audienceMember.name}
                    </div>
                  </div>
                </TableData>
                <TableData>
                  <EmailVerificationStatus email={audienceMember.email} isVerified={audienceMember.email_is_verified} />
                </TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={!!audienceMember.ticket_count}/>
                </TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={!!audienceMember.membership_count}/>
                </TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={!!audienceMember.visit_count}/>
                </TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={!!audienceMember.app_permissions}/>
                </TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={!!audienceMember.linked_account_count}/>
                </TableData>
                <TableData>{formattedDateTime(audienceMember.created_at) || 'Never'}</TableData>
                <TableData>{formattedDateTime(audienceMember.last_logged_in_at) || 'Never'}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <AudienceMemberContextMenu
                    audienceMember={audienceMember}
                    onDelete={() => setDeletingAudienceMember(audienceMember)}
                  />
                </TableData>

                {/* Delete confirmation modal */}
                <DeleteUser
                  user={deletingAudienceMember}
                  show={audienceMember === deletingAudienceMember}
                  onCancel={() => setDeletingAudienceMember(null)}
                  onDelete={() => {
                    setDeletingAudienceMember(null)
                    navigate('/audience')
                  }}
                  onDeleteFail={() => setDeletingAudienceMember(null)}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default AudienceMemberList;